import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { HudCard } from '../../hud';
import ProjectGenerationGallery from '../ProjectGenerationGallery';
import AnnotationDialog from '../AnnotationDialog';
import VariationDialog from '../VariationDialog';
import { useRefsChanged } from '../../../lib/generationEvents';
import { API, STAGES } from './constants';
import LotHeaderBar from './LotHeaderBar';
import PipelineStepRail from './PipelineStepRail';
import ActiveInputsPanel from './ActiveInputsPanel';
import StepActionLauncher from './StepActionLauncher';
import ReferencePickerDialog from './ReferencePickerDialog';
import BatchAndFpsDrawer from './BatchAndFpsDrawer';

const opts = { withCredentials: true };

/** V15.21 — Cockpit Production : un lot, 4 étapes, entrées visibles, actions explicites, galerie du lot. */
export const ProductionCockpit = ({ projectId, galleryNonce, onJobLaunched }) => {
  const [lots, setLots] = useState([]);
  const [lotId, setLotId] = useState(() => { try { return new URLSearchParams(window.location.search).get('lot_id') || localStorage.getItem(`pe_last_lot_${projectId}`) || ''; } catch { return ''; } });
  const [data, setData] = useState(null);
  const [values, setValues] = useState(null);
  const [lotJobs, setLotJobs] = useState([]);
  const [stage, setStage] = useState(null);
  const [stageAuto, setStageAuto] = useState(true);
  const [projectView, setProjectView] = useState(false);
  const [drawer, setDrawer] = useState(null); // 'batch' | 'fps' | null
  const [refPick, setRefPick] = useState(null); // stage id
  const [annotate, setAnnotate] = useState(null);
  const [variation, setVariation] = useState(null);
  const [nonce, setNonce] = useState(0);

  const loadLots = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/lots-progress`, opts);
      const list = r.data?.lots || [];
      setLots(list);
      setLotId((cur) => (list.some((l) => l.lot_id === cur) ? cur : (list.find((l) => l.has_structure) || list[0])?.lot_id || ''));
    } catch { setLots([]); }
  }, [projectId]);

  const loadLot = useCallback(async () => {
    if (!lotId) return;
    try {
      const [si, gens] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/lots/${lotId}/stage-inputs`, opts),
        axios.get(`${API}/projects/${projectId}/generations`, opts).catch(() => ({ data: { items: [] } })),
      ]);
      setData(si.data);
      setLotJobs((gens.data?.items || []).filter((j) => j.lot_id === lotId));
    } catch { setData(null); }
  }, [projectId, lotId]);

  useEffect(() => { loadLots(); }, [loadLots, nonce]);
  useEffect(() => { loadLot(); }, [loadLot, nonce, galleryNonce]);
  useEffect(() => {
    axios.get(`${API}/projects/${projectId}/prompt-config`, opts).then((r) => setValues(r.data?.values || {})).catch(() => setValues({}));
  }, [projectId]);
  useEffect(() => { if (lotId) { try { localStorage.setItem(`pe_last_lot_${projectId}`, lotId); } catch { /* no-op */ } setStageAuto(true); } }, [lotId, projectId]);
  useEffect(() => { if (data && stageAuto) { setStage(data.next_stage || 'whitebox'); setStageAuto(false); } }, [data, stageAuto]);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);
  useRefsChanged(useCallback((d) => { if (d.lotId && d.lotId !== lotId && lots.some((l) => l.lot_id === d.lotId)) setLotId(d.lotId); if (d.nextStage) setStage(d.nextStage); refresh(); }, [lotId, lots, refresh]));
  const hasPending = useMemo(() => Object.values(data?.stages || {}).some((s) => s.counts?.pending > 0), [data]);
  useEffect(() => { if (!hasPending) return undefined; const t = setInterval(refresh, 8000); return () => clearInterval(t); }, [hasPending, refresh]);

  const lot = lots.find((l) => l.lot_id === lotId) || null;
  const launched = (job) => { onJobLaunched?.(job); refresh(); setTimeout(refresh, 45000); };

  return (
    <div className="space-y-4" data-testid="production-cockpit">
      <LotHeaderBar projectId={projectId} lots={lots} activeLotId={lotId} onSelectLot={setLotId} onRefresh={refresh} projectView={projectView} onToggleProjectView={() => setProjectView((v) => !v)}
                    onOpenBatch={() => setDrawer('batch')} onOpenFps={() => setDrawer('fps')} />
      {projectView ? (
        <ProjectGenerationGallery projectId={projectId} refreshNonce={galleryNonce + nonce} />
      ) : !lotId ? (
        <HudCard variant="glass"><p className="text-[12.5px] py-8 text-center" style={{ color: 'var(--c-text-faint)' }}>Aucun lot : créez un lot et tracez sa structure dans l'Éditeur Plan.</p></HudCard>
      ) : (
        <div className="grid grid-cols-12 gap-4 items-start">
          <div className="col-span-12 xl:col-span-8 space-y-4">
            <HudCard variant="glass" bracket>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className="hud-eyebrow hud-eyebrow-gold">Pipeline du lot {lot?.lot_number || ''}</span>
                  <p className="mt-0.5 font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 18 }}>4 étapes · chaque étape s'appuie sur la référence ancrée de la précédente</p>
                </div>
                {data && <span className="text-[11px]" style={{ color: 'var(--c-text-2)' }}>Prochaine étape conseillée : <strong style={{ color: 'var(--c-gold)' }}>{STAGES.find((s) => s.id === data.next_stage)?.label}</strong></span>}
              </div>
              <PipelineStepRail stages={data?.stages} activeStage={stage} onSelect={(s) => { setStage(s); setStageAuto(false); }} />
            </HudCard>
            {data && stage && (
              <HudCard variant="glass">
                <div className="space-y-4">
                  <ActiveInputsPanel projectId={projectId} lotId={lotId} stage={stage} data={data} onChangeReference={(s) => setRefPick(s)} onRefresh={refresh} />
                  <StepActionLauncher projectId={projectId} lotId={lotId} lot={lot} stage={stage} data={data} values={values} lotJobs={lotJobs}
                                      onLaunched={launched} onOpenAnnotation={setAnnotate} onOpenVariation={setVariation} onChangeReference={(s) => setRefPick(s)} />
                </div>
              </HudCard>
            )}
          </div>
          <div className="col-span-12 xl:col-span-4">
            <ProjectGenerationGallery projectId={projectId} refreshNonce={galleryNonce + nonce} lotId={lotId} stageFilter={stage || 'all'} compact />
            {stage && <p className="text-[10.5px] mt-1.5 px-1" style={{ color: 'var(--c-text-faint)' }}>Filtré sur l'étape active · cliquez une autre étape du rail pour voir ses rendus.</p>}
          </div>
        </div>
      )}
      <BatchAndFpsDrawer open={!!drawer} mode={drawer} onClose={() => setDrawer(null)} projectId={projectId} onJobLaunched={launched} />
      {refPick && <ReferencePickerDialog open onClose={() => setRefPick(null)} projectId={projectId} lotId={lotId} stage={refPick} jobs={lotJobs}
                                         currentId={data?.stages?.[refPick]?.reference?.job_id} onChanged={refresh} />}
      <AnnotationDialog open={!!annotate} onClose={() => setAnnotate(null)} projectId={projectId} generation={annotate} onSubmitted={() => { setAnnotate(null); launched(); }} />
      {variation && <VariationDialog projectId={projectId} parentItem={variation} onClose={() => setVariation(null)} onCreated={() => { setVariation(null); launched(); }} />}
    </div>
  );
};

export default ProductionCockpit;
